import { Link } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CheckCheckIcon, CheckIcon, Loader2Icon, LogInIcon, MessageSquareIcon, Trash2Icon, XIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { ActionStatusDialog } from "@/shared/components/ActionStatusDialog";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { useActionStatusDialog } from "@/shared/hooks/useActionStatusDialog";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { acceptInvitation, declineInvitation, deleteNotification, markNotificationAsRead } from "@/features/notifications/services/notifications";
import { AppNotification } from "@/features/notifications/types";
import { cn } from "@/shared/lib/utils/cn";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";

import { INVITE_STATUS_CONFIG, TYPE_CONFIG } from "../config";
import { actorInitials, buildMessage, commentExcerpt, isUnread } from "../helpers";

export function NotificationCard({ notification, permissions }: { notification: AppNotification; permissions: string[] }) {
  const queryClient = useQueryClient();
  const cfg = TYPE_CONFIG[notification.type];
  const Icon = cfg.icon;
  const unread = isUnread(notification);

  const mutation = useMutation({
    mutationFn: () => deleteNotification({ id: notification.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS] });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptInvitation({ id: notification.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PROJECTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS] });
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => declineInvitation({ id: notification.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS] });
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: () => markNotificationAsRead({ id: notification.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS] });
    },
  });

  const actor =
    notification.invitationData?.sentBy ?? notification.projectRemovalData?.removedBy ?? notification.projectRoleUpdateData?.updatedBy ?? notification.ticketAssignmentData?.assignedBy ?? notification.ticketUpdateData?.updatedBy ?? notification.commentReplyData?.repliedBy;

  const initials = actor ? actorInitials(actor) : "?";

  const inviteStatus = notification.invitationData?.invitationStatus;
  const inviteStatusCfg = inviteStatus ? INVITE_STATUS_CONFIG[inviteStatus] : null;
  const isPendingInvite = notification.type === "ProjectInvitation" && inviteStatus === "Pending";
  const isResolvedInvite = notification.type === "ProjectInvitation" && inviteStatus && inviteStatus !== "Pending";

  const project = notification.invitationData?.project ?? notification.projectRemovalData?.project ?? notification.projectRoleUpdateData?.project;

  const reply = notification.type === "CommentReply" ? notification.commentReplyData : undefined;

  const acceptStatus = useActionStatusDialog(acceptMutation);
  const declineStatus = useActionStatusDialog(declineMutation);
  const deleteStatus = useActionStatusDialog(mutation);
  const markAsReadStatus = useActionStatusDialog(markAsReadMutation);

  const canAccept = permissions.includes(PERMISSIONS.USER_INVITATIONS_ACCEPT);
  const canDecline = permissions.includes(PERMISSIONS.USER_INVITATIONS_DECLINE);
  const canMarkAsRead = permissions.includes(PERMISSIONS.USER_NOTIFICATIONS_MARK_AS_READ);
  const canDelete = permissions.includes(PERMISSIONS.USER_NOTIFICATIONS_DELETE);

  const formattedDate = formatRelativeDate(notification.createdAt);
  const absoluteDate = formatAbsoluteDateTime(notification.createdAt);

  const anyPending = mutation.isPending || acceptMutation.isPending || declineMutation.isPending || markAsReadMutation.isPending;

  return (
    <div className={cn("group relative flex gap-3 border-l-2 border-transparent pl-3.5 pr-4 py-3.5 transition-colors hover:bg-accent/40", unread && "border-primary bg-primary/[0.03]", anyPending && "opacity-60 pointer-events-none")}>
      {/* Avatar + type icon */}
      <div className="relative shrink-0">
        <Avatar size="default">
          <AvatarImage src="" alt={initials} />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className={cn("absolute -bottom-1 -right-1 flex size-4.5 items-center justify-center ring-2 ring-background", cfg.bgClass)}>
          {anyPending ? <Loader2Icon className="size-2.5 animate-spin text-muted-foreground" /> : <Icon className={cn("size-2.5", cfg.iconClass)} />}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1.5 min-w-0">
        <p className="text-sm leading-snug">{buildMessage(notification)}</p>

        {/* Reply preview */}
        {reply?.comment && <p className="border-l-2 border-border pl-2.5 text-xs italic leading-relaxed text-muted-foreground">{commentExcerpt(reply.comment.body)}</p>}

        {/* Metadata row: type label, resolved invite status, timestamp */}
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {isResolvedInvite && inviteStatusCfg ? (
            <span className={cn("inline-flex items-center gap-1 font-semibold uppercase tracking-wider", inviteStatusCfg.className)}>
              {inviteStatus === "Accepted" ? <CheckIcon className="size-3" /> : <XIcon className="size-3" />}
              {inviteStatusCfg.label}
            </span>
          ) : (
            <span className="font-medium uppercase tracking-wider text-[10px]">{cfg.label}</span>
          )}
          <span aria-hidden>·</span>
          <Tooltip delayDuration={400}>
            <TooltipTrigger asChild>
              <span className="w-fit">{formattedDate}</span>
            </TooltipTrigger>
            <TooltipContent>{absoluteDate}</TooltipContent>
          </Tooltip>
        </div>

        {/* Reply link */}
        {reply?.ticket && (
          <Button variant="outline" size="sm" className="mt-1 h-7 w-fit px-3 text-xs" asChild>
            <Link to={`/projects/${reply.project.id}/tickets/${reply.ticket.id}`}>
              <MessageSquareIcon className="size-3" />
              View reply
            </Link>
          </Button>
        )}

        {/* Pending invite actions */}
        {isPendingInvite && project && (
          <div className="flex items-center gap-2 mt-1">
            <PermissionTooltip allowed={canAccept} message="You don't have permission to accept invitations.">
              <Button size="sm" className="h-7 px-3 text-xs" onClick={() => acceptMutation.mutate()} disabled={!canAccept || acceptMutation.isPending || mutation.isPending}>
                <LogInIcon className="size-3" />
                {acceptMutation.isPending ? "Accepting..." : "Accept"}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip allowed={canDecline} message="You don't have permission to decline invitations.">
              <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => declineMutation.mutate()} disabled={!canDecline || declineMutation.isPending || acceptMutation.isPending || mutation.isPending}>
                <XIcon className="size-3" />
                {declineMutation.isPending ? "Declining..." : "Decline"}
              </Button>
            </PermissionTooltip>
          </div>
        )}
      </div>

      {/* Row actions */}
      <div className="flex shrink-0 flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {unread && (
          <PermissionTooltip allowed={canMarkAsRead} message="You don't have permission to mark notifications as read.">
            <button
              onClick={() => markAsReadMutation.mutate()}
              disabled={!canMarkAsRead || markAsReadMutation.isPending || mutation.isPending}
              className="flex size-7 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
              title="Mark as read"
            >
              {markAsReadMutation.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : <CheckCheckIcon className="size-3.5" />}
            </button>
          </PermissionTooltip>
        )}
        <PermissionTooltip allowed={canDelete} message="You don't have permission to dismiss notifications.">
          <button onClick={() => mutation.mutate()} disabled={!canDelete || mutation.isPending} className="flex size-7 items-center justify-center text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40" title="Dismiss">
            {mutation.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : <Trash2Icon className="size-3.5" />}
          </button>
        </PermissionTooltip>
      </div>

      <ActionStatusDialog
        open={acceptStatus.open}
        phase={acceptStatus.phase}
        onClose={acceptStatus.close}
        onRetry={() => acceptMutation.mutate()}
        error={acceptMutation.error}
        successTitle={project ? `You've joined ${project.name}` : "Invitation accepted"}
        successDescription={project ? `You now have access to ${project.name}.` : undefined}
        errorTitle="Unable to accept invitation"
      />

      <ActionStatusDialog
        open={declineStatus.open}
        phase={declineStatus.phase}
        onClose={declineStatus.close}
        onRetry={() => declineMutation.mutate()}
        error={declineMutation.error}
        successTitle="Invitation declined"
        successDescription={project ? `You won't be added to ${project.name}.` : undefined}
        errorTitle="Unable to decline invitation"
      />

      <ActionStatusDialog open={markAsReadStatus.open} phase={markAsReadStatus.phase} onClose={markAsReadStatus.close} onRetry={() => markAsReadMutation.mutate()} error={markAsReadMutation.error} successTitle="Marked as read" errorTitle="Unable to mark as read" />

      <ActionStatusDialog open={deleteStatus.open} phase={deleteStatus.phase} onClose={deleteStatus.close} onRetry={() => mutation.mutate()} error={mutation.error} successTitle="Notification dismissed" errorTitle="Unable to dismiss notification" />
    </div>
  );
}
